import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { Globe, ShieldCheck, MapPin, Target, Eye, Sparkles, Shield, Users, Award, GraduationCap, Lightbulb } from 'lucide-react';
const coreValues = [{
  icon: Sparkles,
  label: 'Passion for Excellence'
}, {
  icon: Shield,
  label: 'Accountability'
}, {
  icon: Users,
  label: 'Collaboration'
}, {
  icon: Award,
  label: 'Integrity'
}, {
  icon: GraduationCap,
  label: 'Learning'
}, {
  icon: Lightbulb,
  label: 'Innovation'
}];
// Press features — outbound search links so we always land on the publication's own coverage
type Press = {
  name: string;
  href: string;
  style: string; // visual identity treatment per publication
};
// Google site-scoped news queries — guaranteed to land on real coverage on the
// publication's own domain. Falls back gracefully if none exists for that outlet.
const pressFeatures: Press[] = [{
  name: 'PUNCH',
  href: 'https://www.google.com/search?q=%22Zylus+Homes%22+site%3Apunchng.com&tbm=nws',
  style: 'font-serif font-black tracking-tight text-red-700'
}, {
  name: 'Vanguard',
  href: 'https://www.google.com/search?q=%22Zylus+Homes%22+site%3Avanguardngr.com&tbm=nws',
  style: 'font-serif italic font-bold text-red-800'
}, {
  name: 'BusinessDay',
  href: 'https://www.google.com/search?q=%22Zylus+Homes%22+site%3Abusinessday.ng&tbm=nws',
  style: 'font-sans font-bold tracking-tight text-blue-900'
}, {
  name: 'Nairametrics',
  href: 'https://www.google.com/search?q=%22Zylus+Homes%22+site%3Anairametrics.com&tbm=nws',
  style: 'font-sans font-bold tracking-tight text-emerald-700'
}, {
  name: 'The Sun',
  href: 'https://www.google.com/search?q=%22Zylus+Homes%22+site%3Asunnewsonline.com&tbm=nws',
  style: 'font-serif font-black italic text-amber-600'
}];
// Globe locations Zylus operates in (lat/lng-style positioning is faked with %)
const locations = [{
  city: 'Lagos',
  x: 49,
  y: 60,
  delay: 0
}, {
  city: 'Abuja',
  x: 51,
  y: 56,
  delay: 0.4
}, {
  city: 'Ibadan',
  x: 48,
  y: 60,
  delay: 0.8
}, {
  city: 'Enugu',
  x: 52,
  y: 60,
  delay: 1.2
}, {
  city: 'London',
  x: 47,
  y: 30,
  delay: 1.6
}, {
  city: 'New York',
  x: 22,
  y: 38,
  delay: 2
}, {
  city: 'Dubai',
  x: 62,
  y: 48,
  delay: 2.4
}, {
  city: 'Toronto',
  x: 24,
  y: 32,
  delay: 2.8
}];
function AnimatedGlobe() {
  return <div className="relative w-full aspect-square max-w-[520px] mx-auto" data-id="element-1507">
      {/* Subtle bg glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#F5B800_0%,transparent_60%)] opacity-10 blur-3xl" data-id="element-1508" />

      {/* Outer rotating ring (slow) */}
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 60,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute inset-0 rounded-full border border-dashed border-ink/15" data-id="element-1509" />

      {/* Middle ring (counter) */}
      <motion.div animate={{
      rotate: -360
    }} transition={{
      duration: 80,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute inset-[8%] rounded-full border border-ink/10" data-id="element-1510">
        {/* Orbiting dot */}
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-gold shadow-[0_0_12px_rgba(245,184,0,0.6)]" data-id="element-1511" />
      </motion.div>

      {/* The globe itself */}
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 90,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute inset-[14%] rounded-full overflow-hidden shadow-2xl bg-ink" data-id="element-1512">
        {/* Latitude lines */}
        {[20, 35, 50, 65, 80].map(y => <div key={y} className="absolute left-0 right-0 border-t border-gold/15" style={{
        top: `${y}%`,
        transform: `scaleX(${Math.sin(y / 100 * Math.PI)})`
      }} data-id="element-1513" />)}
        {/* Longitude lines */}
        {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="absolute top-0 bottom-0 left-1/2 border-l border-gold/15 origin-center" style={{
        transform: `rotate(${i * 30}deg)`
      }} data-id="element-1514" />)}

        {/* Dotted world map texture using radial gradients to suggest continents */}
        <div className="absolute inset-0 opacity-50" style={{
        backgroundImage: `
              radial-gradient(circle at 50% 60%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 48% 58%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 51% 62%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 47% 64%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 22% 40%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 25% 38%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 28% 42%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 62% 48%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 70% 65%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 75% 70%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 80% 50%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 30% 70%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 35% 75%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 50% 25%, #F5B800 0, transparent 1.5px),
              radial-gradient(circle at 45% 22%, #F5B800 0, transparent 1.5px)
            `,
        backgroundSize: '100% 100%'
      }} data-id="element-1515" />

        {/* Subtle vertical sheen */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" data-id="element-1516" />
      </motion.div>

      {/* Location pulses (NOT rotating with globe — fixed overlay) */}
      <div className="absolute inset-[14%] rounded-full overflow-hidden pointer-events-none" data-id="element-1517">
        {locations.map(loc => <motion.div key={loc.city} className="absolute" style={{
        left: `${loc.x}%`,
        top: `${loc.y}%`,
        transform: 'translate(-50%, -50%)'
      }} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: loc.delay,
        duration: 0.6
      }} data-id="element-1518">
            <div className="relative" data-id="element-1519">
              <motion.div animate={{
            scale: [1, 2.5, 1],
            opacity: [0.6, 0, 0.6]
          }} transition={{
            duration: 2.4,
            repeat: Infinity,
            delay: loc.delay
          }} className="absolute inset-0 w-3 h-3 rounded-full bg-gold" data-id="element-1520" />
              <div className="relative w-3 h-3 rounded-full bg-gold border-2 border-white shadow-md" data-id="element-1521" />
            </div>
          </motion.div>)}
      </div>

      {/* Center Z badge */}
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      delay: 0.4,
      type: 'spring',
      stiffness: 120
    }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gold shadow-[0_8px_32px_rgba(245,184,0,0.4)] flex items-center justify-center z-10 border-4 border-white" data-id="element-1522">
        <span className="font-serif text-3xl text-white" data-id="element-1523">Z</span>
      </motion.div>

      {/* Floating city chips around the globe */}
      <motion.div animate={{
      y: [0, -8, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity
    }} className="absolute top-2 right-0 bg-white rounded-full shadow-lg pl-2 pr-3 py-1.5 flex items-center gap-1.5 z-20" data-id="element-1524">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-1525" />
        <span className="text-[10px] font-bold tracking-widest uppercase text-ink" data-id="element-1526">
          Lagos · Live
        </span>
      </motion.div>
      <motion.div animate={{
      y: [0, 8, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      delay: 1
    }} className="absolute bottom-4 left-0 bg-ink rounded-full shadow-lg px-3 py-1.5 z-20" data-id="element-1527">
        <span className="text-[10px] font-bold tracking-widest uppercase text-gold" data-id="element-1528">
          Diaspora · 12 countries
        </span>
      </motion.div>
      <motion.div animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 4.5,
      repeat: Infinity,
      delay: 0.5
    }} className="absolute top-1/2 -right-2 bg-white rounded-full shadow-lg pl-2 pr-3 py-1.5 flex items-center gap-1.5 z-20" data-id="element-1529">
        <MapPin className="w-3 h-3 text-gold" data-id="element-1530" />
        <span className="text-[10px] font-bold tracking-widest uppercase text-ink" data-id="element-1531">
          4 cities
        </span>
      </motion.div>
    </div>;
}
export function WhoWeAre() {
  return <div className="pt-28 pb-24" data-id="element-1532">
      <PageHeader eyebrow="About Us" title="Who We Are" subtitle="Pioneering accessible real estate and property ownership across Africa and beyond." breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Who We Are'
    }]} data-id="element-1533" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-12 mt-16" data-id="element-1534">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1535">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="space-y-6 text-ink/80 text-lg leading-relaxed" data-id="element-1536">
            <p data-id="element-1537">
              <strong className="text-ink font-semibold" data-id="element-1538">Zylus Homes</strong> is
              a cutting-edge real estate and investment company dedicated to
              expanding access to home and property ownership across Africa and
              the diaspora. With over 5 years of credibility, we serve clients
              across the globe.
            </p>
            <p data-id="element-1539">
              We handle the entire real estate lifecycle — from land acquisition
              and landbanking, to property development and construction
              management — empowering clients to make smarter buying and
              investment decisions.
            </p>
            <p data-id="element-1540">
              At Zylus, we take pride in satisfying our clients' ongoing needs,
              anchored in our core value:{' '}
              <strong className="text-gold-dark font-semibold" data-id="element-1541">
                integrity
              </strong>
              .
            </p>
            <p data-id="element-1542">
              Our success stories have been featured in Nigeria's most reputable
              publications. We exist to put smiles on our customers' faces.
            </p>

            <div className="pt-6 grid grid-cols-2 gap-6" data-id="element-1543">
              <div className="flex items-start gap-3" data-id="element-1544">
                <Globe className="w-6 h-6 text-gold shrink-0" data-id="element-1545" />
                <div data-id="element-1546">
                  <div className="font-semibold text-ink text-base mb-1" data-id="element-1547">
                    Global Reach
                  </div>
                  <div className="text-sm text-ink/60" data-id="element-1548">
                    Serving clients across the diaspora.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1549">
                <ShieldCheck className="w-6 h-6 text-gold shrink-0" data-id="element-1550" />
                <div data-id="element-1551">
                  <div className="font-semibold text-ink text-base mb-1" data-id="element-1552">
                    Integrity First
                  </div>
                  <div className="text-sm text-ink/60" data-id="element-1553">
                    Transparent processes and verified titles.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Animated Globe */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="relative flex items-center justify-center py-8" data-id="element-1554">
            <AnimatedGlobe data-id="element-1555" />
          </motion.div>
        </div>

        {/* Stats Strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-24 bg-ink rounded-3xl p-10 md:p-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 text-center divide-x divide-white/10" data-id="element-1556">
          <div data-id="element-1557">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1558">
              5+
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1559">
              Years Credibility
            </div>
          </div>
          <div data-id="element-1560">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1561">
              1000+
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1562">
              Happy Clients
            </div>
          </div>
          <div data-id="element-1563">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1564">
              6+
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1565">
              Premium Estates
            </div>
          </div>
          <div data-id="element-1566">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1567">
              4
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1568">
              Subsidiaries
            </div>
          </div>
        </motion.div>

        {/* Mission · Vision · Core Values */}
        <div className="mt-24" data-id="element-1569">
          <div className="text-center mb-12" data-id="element-1570">
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-1571">
              What Guides Us
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-3xl md:text-5xl font-serif text-ink tracking-tight" data-id="element-1572">
              The principles behind every estate.
            </motion.h2>
          </div>

          {/* Mission + Vision split */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5" data-id="element-1573">
            {/* Mission card */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative bg-white rounded-3xl border border-ink/5 p-8 md:p-10 overflow-hidden group hover:shadow-lg transition-shadow" data-id="element-1574">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/5 blur-3xl pointer-events-none" data-id="element-1575" />
              <div className="relative" data-id="element-1576">
                <div className="flex items-center gap-3 mb-6" data-id="element-1577">
                  <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shadow-md" data-id="element-1578">
                    <Target className="w-6 h-6 text-white" strokeWidth={1.8} data-id="element-1579" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-gold-dark" data-id="element-1580">
                    Our Mission
                  </div>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight mb-4" data-id="element-1581">
                  Investment opportunities for Nigerians at home and abroad.
                </h3>
                <p className="text-ink/65 leading-relaxed" data-id="element-1582">
                  To provide real estate investment opportunities to individuals
                  in Nigeria and the diaspora through our well-structured
                  payment plans and exceptional service.
                </p>
              </div>
            </motion.div>

            {/* Vision card */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="relative bg-ink text-white rounded-3xl p-8 md:p-10 overflow-hidden group" data-id="element-1583">
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full border border-gold/20 pointer-events-none" data-id="element-1584" />
              <motion.div animate={{
              rotate: -360
            }} transition={{
              duration: 60,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute -top-16 -left-16 w-56 h-56 rounded-full border border-gold/10 pointer-events-none" data-id="element-1585" />
              <div className="relative" data-id="element-1586">
                <div className="flex items-center gap-3 mb-6" data-id="element-1587">
                  <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shadow-md" data-id="element-1588">
                    <Eye className="w-6 h-6 text-white" strokeWidth={1.8} data-id="element-1589" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-gold" data-id="element-1590">
                    Our Vision
                  </div>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-4" data-id="element-1591">
                  Homeownership, attainable for every individual in need.
                </h3>
                <p className="text-white/70 leading-relaxed" data-id="element-1592">
                  We strive to make homeownership attainable for every
                  individual in need — across Nigeria, across Africa, across the
                  diaspora.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Core Values strip */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-bone-dark/40 rounded-3xl border border-ink/5 p-8 md:p-10 overflow-hidden" data-id="element-1593">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4" data-id="element-1594">
              <div data-id="element-1595">
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-gold-dark mb-2" data-id="element-1596">
                  Core Values
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight" data-id="element-1597">
                  Six principles. One promise.
                </h3>
              </div>
              <div className="text-sm text-ink/55 max-w-xs" data-id="element-1598">
                The principles that shape every decision, every estate, every
                client interaction.
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-1599">
              {coreValues.map((v, i) => <motion.div key={v.label} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.06
            }} whileHover={{
              y: -4
            }} className="group relative bg-white rounded-2xl p-5 border border-ink/5 hover:border-gold/40 hover:shadow-md transition-all flex flex-col items-center text-center" data-id="element-1600">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-3 group-hover:bg-gold transition-colors" data-id="element-1601">
                    <v.icon className="w-5 h-5 text-gold-dark group-hover:text-white transition-colors" data-id="element-1602" />
                  </div>
                  <div className="font-serif text-base text-ink leading-tight" data-id="element-1603">
                    {v.label}
                  </div>
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-gold w-0 group-hover:w-1/2 transition-all duration-500" data-id="element-1604" />
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        {/* Featured In — Nigerian press */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-20" data-id="element-1605">
          <div className="text-center mb-10" data-id="element-1606">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-ink/40 mb-2" data-id="element-1607">
              As Featured In
            </div>
            <div className="text-sm text-ink/55" data-id="element-1608">
              Trusted by Nigeria's leading newsrooms
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-ink/5 px-6 md:px-12 py-10 shadow-sm" data-id="element-1609">
            <div className="flex flex-wrap items-center justify-center gap-x-10 md:gap-x-16 gap-y-6" data-id="element-1610">
              {pressFeatures.map((p, i) => <motion.a key={p.name} href={p.href} target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.08
            }} whileHover={{
              y: -2
            }} className="group inline-flex flex-col items-center gap-1.5 opacity-60 hover:opacity-100 transition-opacity" aria-label={`Read about Zylus on ${p.name}`} data-id="element-1611">
                  <span className={`text-2xl md:text-3xl ${p.style} grayscale group-hover:grayscale-0 transition-all`} data-id="element-1612">
                    {p.name}
                  </span>
                  <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-ink/30 group-hover:text-gold transition-colors" data-id="element-1613">
                    Read coverage →
                  </span>
                </motion.a>)}
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}